import Image1 from '../assets/Image1.png';
import Image2 from '../assets/Image2.jpg';
import Image3 from '../assets/Image3.png';
  
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Edit, Trash2, Plus, Camera, Upload, Menu, X } from 'lucide-react';
import AnimatedTitle from './AnimatedTitle';
import AnimatedSubtitle from './AnimatedSubtitle';

interface HeroImage {
  id: number;
  imageSrc: string;
  size: number;
  finalTop: string;
  finalLeft: string;
}

interface HeroSectionData {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  buttonText: string;
  logoSrc?: string;
  images?: HeroImage[];
}

interface HeroImageFormData {
  id: number;
  imageSrc: string;
  size: number;
  finalTop: string;
  finalLeft: string;
}


const HeroSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRefs = useRef<(number | NodeJS.Timeout | null)[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State management
  // State management
  const [heroData, setHeroData] = useState<HeroSectionData>({
    id: 1,
    title: "Empower",
    subtitle: "Nurturing Excellence, Building Characte",
    description: "Ever Green Senior Secondary School, located in Sri Ganga Vihar Colony, Beri Parao, Nainital, is a distinguished educational institution offering comprehensive education from Pre-K to Class XII. Our school combines academic excellence with character development through a balanced curriculum, modern facilities, and experienced faculty. We emphasize both traditional values and contemporary learning, providing students with opportunities in sports, cultural activities, and educational excursions. With consistently strong board results and a supportive environment, we nurture students to become responsible global citizens while maintaining our commitment to environmental awareness and sustainable practices in the beautiful hills of Uttarakhand.",
    buttonText: "Admission Open",
    images: [
      { id: 1, imageSrc: Image1, size: 300, finalTop: "10%", finalLeft: "35%" },
      { id: 2, imageSrc: Image2, size: 200, finalTop: "50%", finalLeft: "65%" },
      { id: 3, imageSrc: Image3, size: 200, finalTop: "40%", finalLeft: "20%" },
    ]
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isImagesLoaded, setIsImagesLoaded] = useState(false);
  const [animationStarted, setAnimationStarted] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState<HeroImage | null>(null);

  // Form states
  const [formData, setFormData] = useState<Partial<HeroSectionData>>({});
  const [imageFormData, setImageFormData] = useState<HeroImageFormData>({
    id: 0,
    imageSrc: '',
    size: 200,
    finalTop: '20',
    finalLeft: '60'
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  useEffect(() => {
    setIsImagesLoaded(true);
  }, []);

  // Create items for animation from hero data
  const items = useMemo(() => {
    if (!heroData?.images) return [];

    const heroItems = heroData.images.map(img => ({
      id: `hero-${img.id}`,
      type: 'avatar' as const,
      size: img.size,
      image: img.imageSrc,
      finalTop: img.finalTop,
      finalLeft: img.finalLeft
    }));

    // Add decorative circles for larger screens
    const circles = [
      { id: 'circle1', type: 'circle' as const, size: 80, finalTop: '25%', finalLeft: '65%' },
      { id: 'circle2', type: 'circle' as const, size: 40, finalTop: '70%', finalLeft: '80%' },
      { id: 'circle3', type: 'circle' as const, size: 60, finalTop: '45%', finalLeft: '85%' },
    ];

    return [...heroItems, ...circles];
  }, [heroData]);

  // Animation logic
  const animateItem = useCallback((element: HTMLElement, startX: number, startY: number, targetX: number, targetY: number, duration: number) => {
    if (!element) return null;

    const startTime = performance.now();
    let animationId: number;

    element.style.transform = `translate(0px, 0px) scale(0)`;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
      const easedProgress = easeOut(progress);

      const x = startX + (targetX - startX) * easedProgress;
      const y = startY + (targetY - startY) * easedProgress;
      const scale = easedProgress;

      element.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;

      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        element.style.transform = `translate(${targetX}px, ${targetY}px) scale(1)`;
      }
    };

    animationId = requestAnimationFrame(animate);
    return animationId;
  }, []);

  useEffect(() => {
    if (isImagesLoaded && !animationStarted) {
      const timer = setTimeout(() => {
        setAnimationStarted(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isImagesLoaded, animationStarted]);

  useEffect(() => {
    if (!containerRef.current || !isImagesLoaded || !items.length) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;

    animationRefs.current.forEach(id => {
      if (typeof id === 'number') {
        cancelAnimationFrame(id);
        clearTimeout(id);
      }
    });
    animationRefs.current = [];

    items.forEach((item, index) => {
      const el = document.getElementById(item.id);
      if (!el) return;

      const finalX = (parseFloat(item.finalLeft) / 100) * containerRect.width;
      const finalY = (parseFloat(item.finalTop) / 100) * containerRect.height;

      const deltaX = finalX - centerX;
      const deltaY = finalY - centerY;

      const timeoutId = setTimeout(() => {
        const animationId = animateItem(el, 0, 0, deltaX, deltaY, 2000);
        if (animationId) animationRefs.current.push(animationId);
      }, index * 150);

      animationRefs.current.push(timeoutId);
    });

    return () => {
      animationRefs.current.forEach(id => {
        if (typeof id === 'number') {
          cancelAnimationFrame(id);
          clearTimeout(id);
        }
      });
    };
  }, [items, animateItem, isImagesLoaded]);

  // CRUD Operations
  const openEditDialog = () => {
    setFormData({ ...heroData });
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setFormData({});
  };

  const handleFormChange = (field: keyof HeroSectionData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateHeroSection = () => {
    const updateData = {
      ...heroData,
      ...formData,
      images: heroData?.images || []
    };
    setHeroData(updateData);
    closeEditDialog();
  };

  // Image CRUD operations
  const openImageDialog = (image?: HeroImage) => {
    if (image) {
      setEditingImage(image);
      setImageFormData({
        id: image.id,
        imageSrc: image.imageSrc,
        size: image.size,
        finalTop: image.finalTop.replace('%', ''),
        finalLeft: image.finalLeft.replace('%', '')
      });
      setImagePreview(image.imageSrc);
    } else {
      setEditingImage(null);
      setImageFormData({
        id: 0,
        imageSrc: '',
        size: 200,
        finalTop: '20',
        finalLeft: '60'
      });
      setImagePreview('');
    }
    setImageFile(null);
    setImageDialogOpen(true);
  };

  const closeImageDialog = () => {
    setImageDialogOpen(false);
    setEditingImage(null);
    setImageFormData({
      id: 0,
      imageSrc: '',
      size: 200,
      finalTop: '20',
      finalLeft: '60'
    });
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const saveImage = () => {
    const newImage: HeroImage = {
      id: editingImage ? editingImage.id : Date.now(),
      imageSrc: imagePreview || imageFormData.imageSrc,
      size: imageFormData.size,
      finalTop: `${imageFormData.finalTop}%`,
      finalLeft: `${imageFormData.finalLeft}%`
    };

    let updatedImages: HeroImage[];
    if (editingImage) {
      updatedImages = heroData?.images?.map(img =>
        img.id === editingImage.id ? newImage : img
      ) || [];
    } else {
      updatedImages = [...(heroData?.images || []), newImage];
    }

    setHeroData(prev => ({
      ...prev,
      images: updatedImages
    }));
    closeImageDialog();
  };

  const deleteImage = (imageId: number) => {
    if (!confirm('Are you sure you want to delete this image?')) return;
    
    const updatedImages = heroData?.images?.filter(img => img.id !== imageId) || [];
    setHeroData(prev => ({
      ...prev,
      images: updatedImages
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-red-600 text-center">
          <p className="text-lg font-semibold">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className=" relative overflow-hidden">
      {/* Background Pattern */}

      {/* Admin Controls */}
      {isAdminMode && (
        <>
          {/* Desktop Admin Controls */}
          <div className="hidden md:flex absolute top-6 right-6 z-20 gap-2">
            <button
              onClick={openEditDialog}
              className="p-3 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-white/20"
            >
              <Edit className="w-5 h-5 text-slate-700" />
            </button>
          </div>

          {/* Mobile Admin Menu */}
         {isAdminMode && (
           <div className="md:hidden absolute top-4 right-4 z-20">
           <button
             onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
             className="p-3 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/20"
           >
             {isMobileMenuOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
           </button>
           
           {isMobileMenuOpen && (
             <div className="absolute top-16 right-0 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-white/20 p-2 min-w-[150px]">
               <button
                 onClick={() => {
                   openEditDialog();
                   setIsMobileMenuOpen(false);
                 }}
                 className="w-full flex items-center gap-2 p-3 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
               >
                 <Edit className="w-4 h-4" />
                 Edit Content
               </button>
             </div>
           )}
         </div>
         )}
        </>
      )}

      {/* Main Content Container */}
      <div className="md:w-[80%] w-[90%] flex flex-col lg:flex-row items-center justify-between  min-h-screen  mx-auto px-4 lg:px-8 py-8 lg:py-0">
        
        {/* Left Content */}
        <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left space-y-6 lg:space-y-8 z-10 lg:pr-8">
          
          {/* Title */}
          <div className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black leading-tight">
            <AnimatedTitle />
          </div>

          {/* Subtitle */}
          <div className="max-w-2xl">
            <AnimatedSubtitle />
          </div>

          {/* Description */}
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed px-4 lg:px-0">
            {heroData.description}
          </p>

          {/* CTA Button */}
          <button className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden text-sm sm:text-base">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative z-10">{heroData.buttonText}</span>
          </button>
        </div>

        {/* Right Animation Container */}
        <div className="flex-1 relative w-full lg:w-auto mt-8 lg:mt-0">
          <div
            ref={containerRef}
            className="relative w-full h-64 sm:h-80 md:h-96 lg:h-screen flex justify-center items-center overflow-hidden"
          >
            {items.map((item, index) => {
              // Responsive size adjustments
              const responsiveSize = window.innerWidth < 768 ? item.size * 0.6 : 
                                   window.innerWidth < 1024 ? item.size * 0.8 : item.size;
              return (
                <div
                  id={item.id}
                  key={item.id}
                  className={`absolute rounded-full transition-all duration-300 ${
                    item.type === 'avatar' 
                      ? 'shadow-xl border-2 sm:border-4 border-white/80 backdrop-blur-sm overflow-hidden' 
                      : 'bg-gradient-to-br from-blue-400/20 to-emerald-400/20 shadow-lg opacity-50 lg:opacity-100'
                  }`}
                  style={{
                    width: responsiveSize,
                    height: responsiveSize,
                    top: '50%',
                    left: '50%',
                    transform: 'translate(0, 0) scale(0)',
                    transformOrigin: 'center',
                    zIndex: item.type === 'avatar' ? 2 : 1,
                  }}
                >
                  {item.type === 'avatar' && (
                    <>
                      <img
                        src={item.image}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onLoad={() => setIsImagesLoaded(true)}
                      />

                      {/* Image Admin Controls */}
                      {isAdminMode && (
                        <div className="absolute top-1 right-1 sm:top-2 sm:right-2 flex gap-1 opacity-0 hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => {
                              const heroImage = heroData.images?.find(img => `hero-${img.id}` === item.id);
                              if (heroImage) openImageDialog(heroImage);
                            }}
                            className="p-1 sm:p-2 bg-white/90 backdrop-blur-sm rounded-md sm:rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                          >
                            <Edit className="w-3 h-3 text-slate-700" />
                          </button>
                          <button
                            onClick={() => {
                              const heroImage = heroData.images?.find(img => `hero-${img.id}` === item.id);
                              if (heroImage) deleteImage(heroImage.id);
                            }}
                            className="p-1 sm:p-2 bg-white/90 backdrop-blur-sm rounded-md sm:rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                          >
                            <Trash2 className="w-3 h-3 text-red-600" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Add Image FAB */}
      {isAdminMode && (
        <button
          onClick={() => openImageDialog()}
          className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 p-3 sm:p-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 z-20"
        >
          <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      )}

      {/* Edit Hero Section Modal */}
      {editDialogOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-slate-200">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Edit Hero Section</h2>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle || ''}
                  onChange={(e) => handleFormChange('subtitle', e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  rows={4}
                  value={formData.description || ''}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none text-sm sm:text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Button Text</label>
                <input
                  type="text"
                  value={formData.buttonText || ''}
                  onChange={(e) => handleFormChange('buttonText', e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                />
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={closeEditDialog}
                className="px-4 sm:px-6 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors duration-200 text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={updateHeroSection}
                className="px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 text-sm sm:text-base"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Image Modal */}
      {imageDialogOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-slate-200">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                {editingImage ? 'Edit Image' : 'Add New Image'}
              </h2>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Image Upload */}
              <div className="text-center">
                <label className="block text-sm font-medium text-slate-700 mb-4">Image</label>
                {imagePreview ? (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 sm:w-48 sm:h-48 object-cover rounded-2xl border-2 sm:border-4 border-white shadow-lg"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 p-1 sm:p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      <Camera className="w-3 h-3 sm:w-4 sm:h-4 text-slate-700" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-32 h-32 sm:w-48 sm:h-48 mx-auto border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors duration-200"
                  >
                    <Upload className="w-8 h-8 sm:w-12 sm:h-12 text-slate-400 mb-2" />
                    <p className="text-slate-500 text-sm">Click to upload</p>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {/* Size Slider */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Size: {imageFormData.size}px
                </label>
                <input
                  type="range"
                  min="50"
                  max="500"
                  step="10"
                  value={imageFormData.size}
                  onChange={(e) => setImageFormData(prev => ({ ...prev, size: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Position Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Top Position (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={imageFormData.finalTop}
                    onChange={(e) => setImageFormData(prev => ({ ...prev, finalTop: e.target.value }))}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Left Position (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={imageFormData.finalLeft}
                    onChange={(e) => setImageFormData(prev => ({ ...prev, finalLeft: e.target.value }))}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={closeImageDialog}
                className="px-4 sm:px-6 py-2 text-slate-700 hover:bg-slate-100 rounded-xl transition-colors duration-200 text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={saveImage}
                className="px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 text-sm sm:text-base"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSection;